<script lang="ts">
  import { api } from '../../lib/api/client';
  import Modal from '../../lib/components/Modal.svelte';
  import BarChart from '../../lib/components/health/BarChart.svelte';
  import { decimal, integer, locale, t } from '../../lib/i18n/index.ts';

  // Quality is how well one of the member's phones records (spec 002):
  // accuracy, time between fixes, gaps, how late uploads arrive, the hours of
  // the day with a fix, and battery drain in each mode, over the last 1, 7,
  // or 30 days.
  let { device, onclose }: { device: { id: string; name: string }; onclose: () => void } = $props();

  type Gap = { mode: string; count: number; median_s?: number; p90_s?: number };
  type Drain = { mode: string; hours: number; drain_pct_per_hour?: number };
  type Report = {
    from: string;
    to: string;
    kept_days: number;
    clipped: boolean;
    fixes: number;
    sources: Record<string, number>;
    accuracy: { median_m?: number; p90_m?: number; buckets: { up_to_m?: number; count: number }[] };
    intervals: Gap[];
    gaps: { count: number; total_s: number; longest_s: number };
    upload_delay?: { median_s: number; p90_s: number; max_s: number };
    clamped: number;
    hours: number[];
    battery: { modes: Drain[]; charging_s: number; lowest_pct?: number };
  };

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let days = $state(7);
  let report: Report | null = $state(null);
  let error = $state('');

  $effect(() => {
    const d = days;
    error = '';
    api
      .get<Report>(`/devices/${device.id}/quality?days=${d}&tz=${encodeURIComponent(tz)}`)
      .then((r) => (report = r))
      .catch((e: any) => (error = e.message));
  });

  const MODES: Record<string, string> = {
    rest: t('settingsQualityModeRest'), moving: t('settingsQualityModeMoving'), live: t('settingsQualityModeLive'), activity: t('settingsQualityModeActivity'),
  };
  const SOURCES: Record<string, string> = {
    batch: t('settingsQualitySourceBatch'), heartbeat: t('settingsQualitySourceHeartbeat'), live: t('settingsQualitySourceLive'),
    refresh: t('settingsQualitySourceRefresh'), transition: t('settingsQualitySourceTransition'), activity: t('settingsQualitySourceActivity'),
    shared: t('settingsQualitySourceShared'),
  };

  function secs(s: number | undefined): string {
    if (s == null) return '—';
    if (s < 90) return t('settingsQualitySeconds', { n: integer(Math.round(s)) });
    if (s < 5400) return t('settingsQualityMinutes', { n: integer(Math.round(s / 60)) });
    const h = s / 3600;
    return h < 48 ? t('settingsQualityHours', { n: decimal(h, h < 10 ? 1 : 0) }) : t('settingsQualityDays', { n: Math.round(h / 24) });
  }
  // hourLabel names an hour of the day alone, "6 AM" or "06", as the region writes it.
  const hourFormat = new Intl.DateTimeFormat(locale(), { hour: 'numeric' });
  const hourLabel = (h: number) => hourFormat.format(new Date(2000, 0, 1, h)).replace(/\u202f/g, ' ');
  const dayFormat = new Intl.DateTimeFormat(locale());
</script>

<Modal title={t('settingsQualityTitle', { device: device.name })} {onclose}>
  <div class="flex flex-col gap-4 text-sm">
    <div class="flex gap-2" role="group" aria-label={t('settingsQualityPeriod')}>
      {#each [1, 7, 30] as d (d)}
        <button class="btn btn-sm {days === d ? 'btn-primary' : 'btn-secondary'}" aria-pressed={days === d} onclick={() => (days = d)}>
          {t('settingsQualityLastDays', { days: d })}
        </button>
      {/each}
    </div>
    {#if error}<p style="color: var(--theme-error);">{error}</p>{/if}
    {#if report}
      {#if report.clipped}
        <p class="text-text-muted">{t('settingsQualityClipped', { days: report.kept_days, date: dayFormat.format(new Date(report.from)) })}</p>
      {/if}
      {#if report.fixes === 0}
        <p class="text-text-muted">{t('settingsQualityNoFixes')}</p>
      {:else}
        <section>
          <h3 class="font-medium mb-1">{t('settingsQualityFixes', { count: report.fixes })}</h3>
          <p class="text-text-muted">
            {Object.entries(report.sources).sort((a, b) => b[1] - a[1]).map(([s, n]) => t('settingsQualitySource', { source: SOURCES[s] ?? s, count: integer(n) })).join(' · ')}
            {#if report.clamped} · {t('settingsQualityClamped', { count: report.clamped })}{/if}
          </p>
        </section>

        <section>
          <h3 class="font-medium mb-1">{t('settingsQualityAccuracy')}</h3>
          <p class="text-text-muted mb-2">{t('settingsQualityAccuracySummary', { median: integer(Math.round(report.accuracy.median_m ?? 0)), p90: integer(Math.round(report.accuracy.p90_m ?? 0)) })}</p>
          <BarChart
            values={report.accuracy.buckets.map((b) => b.count)}
            labels={report.accuracy.buckets.map((b, i) => (b.up_to_m != null ? t('settingsQualityBucketUpTo', { m: b.up_to_m }) : t('settingsQualityBucketOver', { m: report!.accuracy.buckets[i - 1]?.up_to_m })))}
            names={report.accuracy.buckets.map((b, i) => (b.up_to_m != null ? t('settingsQualityBucketWithin', { m: b.up_to_m }) : t('settingsQualityBucketOverName', { m: report!.accuracy.buckets[i - 1]?.up_to_m })))}
            color="var(--theme-blue)" caption={t('settingsQualityAccuracyCaption')} format={(v) => t('settingsQualityFixes', { count: v })}
            heading={{ name: t('settingsQualityFixesHeading'), value: t('settingsQualityFixesHeading') }} unit={t('settingsQualityFixesUnit')} />
        </section>

        <section>
          <h3 class="font-medium mb-1">{t('settingsQualityIntervals')}</h3>
          <table class="w-full">
            <thead class="text-text-muted text-left"><tr><th class="font-normal">{t('settingsQualityWhile')}</th><th class="font-normal">{t('settingsQualityUsually')}</th><th class="font-normal">{t('settingsQualityWithin90')}</th></tr></thead>
            <tbody>
              {#each report.intervals.filter((g) => g.count > 0) as g (g.mode)}
                <tr><td>{MODES[g.mode]}</td><td>{secs(g.median_s)}</td><td>{secs(g.p90_s)}</td></tr>
              {/each}
            </tbody>
          </table>
          <p class="text-text-muted mt-1">
            {#if report.gaps.count}
              {t('settingsQualityGaps', { count: report.gaps.count, total: secs(report.gaps.total_s), longest: secs(report.gaps.longest_s) })}
            {:else}
              {t('settingsQualityNoGaps')}
            {/if}
          </p>
        </section>

        {#if report.upload_delay}
          <section>
            <h3 class="font-medium mb-1">{t('settingsQualityUploads')}</h3>
            <p class="text-text-muted">{t('settingsQualityUploadDelay', { median: secs(report.upload_delay.median_s), p90: secs(report.upload_delay.p90_s), latest: secs(report.upload_delay.max_s) })}</p>
          </section>
        {/if}

        <section>
          <h3 class="font-medium mb-1">{t('settingsQualityHoursHeading')}</h3>
          <BarChart
            values={report.hours.map((v) => Math.round(v * 100))}
            ticks={[0, 6, 12, 18].map((h) => ({ at: (h + 0.5) / 24, text: hourLabel(h) }))}
            names={report.hours.map((_, h) => hourLabel(h))}
            color="var(--theme-green)" caption={t('settingsQualityHoursCaption')} format={(v) => t('settingsQualityPercent', { n: v })}
            heading={{ name: t('settingsQualityWithFix'), value: t('settingsQualityWithFix') }} unit="%" steps={[25, 50]} />
        </section>

        <section>
          <h3 class="font-medium mb-1">{t('settingsQualityBattery')}</h3>
          <table class="w-full">
            <thead class="text-text-muted text-left"><tr><th class="font-normal">{t('settingsQualityWhile')}</th><th class="font-normal">{t('settingsQualityDrain')}</th><th class="font-normal">{t('settingsQualityOver')}</th></tr></thead>
            <tbody>
              {#each report.battery.modes.filter((d) => d.hours > 0) as d (d.mode)}
                <tr>
                  <td>{MODES[d.mode]}</td>
                  <td>{d.drain_pct_per_hour != null ? t('settingsQualityPercent', { n: decimal(d.drain_pct_per_hour, 1) }) : t('settingsQualityTooLittle')}</td>
                  <td>{secs(d.hours * 3600)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
          <p class="text-text-muted mt-1">
            {#if report.battery.lowest_pct != null}
              {t('settingsQualityChargingLowest', { time: secs(report.battery.charging_s), percent: report.battery.lowest_pct })}
            {:else}
              {t('settingsQualityCharging', { time: secs(report.battery.charging_s) })}
            {/if}
          </p>
        </section>
      {/if}
    {/if}
  </div>
</Modal>
